import { differenceInCalendarDays } from 'date-fns';

import { Text } from '@reckon-web/text';
import { Flex } from '@reckon-web/flex';
import { Tooltip } from '@reckon-web/tooltip';
import { makeId } from '@reckon-web/utils';

import { DateUtils } from '../../utils/date-utils';
import { GetActivityItem } from '../../data/useGetActivities';
import { NamedUserAvatarWithUser } from '../common/named-user-avatar';

import {
  ActivityCategoryEnum,
  ActivityCategoryEnumValue,
} from './activities-types';
import {
  ActivityCategoryClosed,
  ActivityCategoryCreated,
  ActivityCategoryDeleted,
  ActivityCategoryEntitiesAdded,
  ActivityCategoryEntitiesRemoved,
  ActivityCategoryFieldUpdated,
  ActivityCategoryRecurrenceRemoved,
  ActivityCategoryRecurrenceUpdated,
  ActivityCategoryReopened,
  ActivityCategorySignOffApprove,
  ActivityCategorySignOffDecline,
  ActivityCategorySignOffRequest,
  ActivityCategorySignOffRevoke,
  ActivityCategorySignOffWithdraw,
} from './activity-category-types';

interface IActivityItem {
  activity: GetActivityItem;
  testId?: string;
}

export const ActivityItem = ({
  activity,
  testId = `activity-item`,
}: IActivityItem) => {
  const dateString = () => {
    const currentTime = new Date();
    const difInCalendarDays = Math.abs(
      differenceInCalendarDays(activity.initiatedOn, currentTime)
    );

    if (difInCalendarDays <= 7) {
      return DateUtils.formatDistance(activity.initiatedOn, {
        addSuffix: true,
      });
    } else {
      return DateUtils.formatDateShort(activity.initiatedOn);
    }
  };

  const formattedDate = DateUtils.formatDateTimeShort(activity.initiatedOn);

  const getActivityCategoryLabel = (
    activityCategory: ActivityCategoryEnumValue
  ) => ActivityCategoryEnum.labels[activityCategory];

  const getActivityCategory = () => {
    switch (
      getActivityCategoryLabel(
        activity.activityCategory as ActivityCategoryEnumValue
      )
    ) {
      case 'Closed':
        return <ActivityCategoryClosed activityTarget={activity.targetType} />;
      case 'Created':
        return <ActivityCategoryCreated activityTarget={activity.targetType} />;
      case 'Deleted':
        return <ActivityCategoryDeleted activityTarget={activity.targetType} />;
      case 'EntitiesAdded':
        return <ActivityCategoryEntitiesAdded activity={activity} />;
      case 'EntitiesRemoved':
        return <ActivityCategoryEntitiesRemoved activity={activity} />;
      case 'FieldUpdated':
        return <ActivityCategoryFieldUpdated activity={activity} />;
      case 'RecurrenceRemoved':
        return <ActivityCategoryRecurrenceRemoved />;
      case 'RecurrenceUpdated':
        return <ActivityCategoryRecurrenceUpdated activity={activity} />;
      case 'Reopened':
        return (
          <ActivityCategoryReopened activityTarget={activity.targetType} />
        );
      case 'SignOffApprove':
        return <ActivityCategorySignOffApprove />;
      case 'SignOffDecline':
        return <ActivityCategorySignOffDecline />;
      case 'SignOffRequest':
        return <ActivityCategorySignOffRequest activity={activity} />;
      case 'SignOffRevoke':
        return <ActivityCategorySignOffRevoke />;
      case 'SignOffWithdraw':
        return <ActivityCategorySignOffWithdraw />;
      default:
        return <Text size="small">ActivityCategory = 0</Text>;
    }
  };

  return (
    <Flex direction="column" gap="xsmall" data={{ testid: testId }}>
      <Flex gap="small" alignItems="baseline">
        <NamedUserAvatarWithUser
          userId={activity.initiatedByUserId!}
          avatarSize="xsmall"
          textSize="small"
          testId={makeId(testId, 'avatar')}
        />
        <Tooltip content={formattedDate}>
          <Text size="xsmall" leading="base" color="muted">
            {dateString()}
          </Text>
        </Tooltip>
      </Flex>
      <Flex marginLeft="xxlarge" gap="xlarge" flexShrink={0}>
        {getActivityCategory()}
      </Flex>
    </Flex>
  );
};
